// Schemas for each commercial entity. Reused by CommercialEntity.
export const SCHEMA_CLIENTS = {
  title: "Clientes",
  subtitle: "Empresas y anunciantes",
  endpoint: "com/clients",
  defaults: { status: "active", country: "Bolivia" },
  columns: [
    { key: "name", label: "Cliente" },
    { key: "industry", label: "Rubro" },
    { key: "city", label: "Ciudad" },
    { key: "email", label: "Email" },
    { key: "status", label: "Estado", render: (it) => (
      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${it.status === "active" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{it.status}</span>
    )},
  ],
  fields: [
    { key: "name", label: "Nombre / Empresa", span: 2 },
    { key: "legal_name", label: "Razón social" },
    { key: "nit", label: "NIT" },
    { key: "industry", label: "Rubro" },
    { key: "country", label: "País" },
    { key: "city", label: "Ciudad" },
    { key: "address", label: "Dirección" },
    { key: "phone", label: "Teléfono" },
    { key: "email", label: "Email", type: "email" },
    { key: "website", label: "Sitio web" },
    { key: "status", label: "Estado", type: "select", options: [{ value: "active", label: "Activo" }, { value: "inactive", label: "Inactivo" }] },
    { key: "observations", label: "Observaciones", type: "textarea", span: 2 },
  ],
};

export const SCHEMA_BRANDS = {
  title: "Marcas",
  subtitle: "Marcas vinculadas a clientes y agencias",
  endpoint: "com/brands",
  defaults: { status: "active" },
  columns: [
    { key: "name", label: "Marca" },
    { key: "client_id", label: "Cliente", render: (it, l) => (l.clients || []).find((c) => c.id === it.client_id)?.name || "—" },
    { key: "agency_id", label: "Agencia", render: (it, l) => (l.agencies || []).find((a) => a.id === it.agency_id)?.name || "—" },
    { key: "industry", label: "Rubro" },
    { key: "status", label: "Estado" },
  ],
  fields: [
    { key: "name", label: "Nombre de la marca", span: 2 },
    { key: "client_id", label: "Cliente propietario", type: "select", allowEmpty: true,
      options: (l) => (l.clients || []).map((c) => ({ value: c.id, label: c.name })) },
    { key: "agency_id", label: "Agencia (opcional)", type: "select", allowEmpty: true,
      options: (l) => (l.agencies || []).map((a) => ({ value: a.id, label: a.name })) },
    { key: "industry", label: "Rubro" },
    { key: "logo", label: "Logo (URL o /api/media/...)" },
    { key: "website", label: "Sitio web" },
    { key: "description", label: "Descripción", type: "textarea", span: 2 },
    { key: "status", label: "Estado", type: "select", options: [{ value: "active", label: "Activa" }, { value: "inactive", label: "Inactiva" }] },
    { key: "observations", label: "Observaciones", type: "textarea", span: 2 },
  ],
};

export const SCHEMA_AGENCIES = {
  title: "Agencias",
  subtitle: "Agencias de medios y publicidad",
  endpoint: "com/agencies",
  defaults: { status: "active", country: "Bolivia", commission_pct: 0 },
  columns: [
    { key: "name", label: "Agencia" },
    { key: "city", label: "Ciudad" },
    { key: "phone", label: "Teléfono" },
    { key: "email", label: "Email" },
    { key: "commission_pct", label: "Comisión %", render: (it) => `${Number(it.commission_pct) || 0}%` },
    { key: "status", label: "Estado" },
  ],
  fields: [
    { key: "name", label: "Nombre", span: 2 },
    { key: "legal_name", label: "Razón social" },
    { key: "nit", label: "NIT" },
    { key: "country", label: "País" },
    { key: "city", label: "Ciudad" },
    { key: "address", label: "Dirección" },
    { key: "phone", label: "Teléfono" },
    { key: "email", label: "Email", type: "email" },
    { key: "website", label: "Sitio web" },
    { key: "commission_pct", label: "Comisión por defecto %", type: "number", step: "0.1" },
    { key: "status", label: "Estado", type: "select", options: [{ value: "active", label: "Activa" }, { value: "inactive", label: "Inactiva" }] },
    { key: "observations", label: "Observaciones", type: "textarea", span: 2 },
  ],
};

export const SCHEMA_CONTACTS = {
  title: "Ejecutivos / Contactos",
  subtitle: "Personas de contacto en clientes, marcas y agencias",
  endpoint: "com/contacts",
  defaults: { status: "active", owner_type: "client", commission_pct: 0 },
  filters: [
    { key: "owner_type", label: "Tipo", options: [{ value: "client", label: "Cliente" }, { value: "brand", label: "Marca" }, { value: "agency", label: "Agencia" }] },
    { key: "owner_id", label: "Empresa", options: (l) => [
      ...(l.clients || []).map((c) => ({ value: c.id, label: `Cliente · ${c.name}` })),
      ...(l.agencies || []).map((a) => ({ value: a.id, label: `Agencia · ${a.name}` })),
    ] },
    { key: "brand_id", label: "Marca", options: (l) => (l.brands || []).map((b) => ({ value: b.id, label: b.name })) },
    { key: "commercial_role", label: "Rol comercial", options: [{ value: "Decisor", label: "Decisor" }, { value: "Negociador", label: "Negociador" }, { value: "Influenciador", label: "Influenciador" }, { value: "Operativo", label: "Operativo" }] },
  ],
  columns: [
    { key: "name", label: "Nombre" },
    { key: "role", label: "Cargo" },
    { key: "owner_type", label: "Pertenece a", render: (it, l) => {
      const map = { client: "Cliente", brand: "Marca", agency: "Agencia" };
      const owner = it.owner_type === "agency"
        ? (l.agencies || []).find((a) => a.id === it.owner_id)?.name
        : (l.clients || []).find((c) => c.id === it.owner_id)?.name;
      return `${map[it.owner_type]}: ${owner || "—"}`;
    }},
    { key: "email", label: "Email" },
    { key: "whatsapp", label: "WhatsApp" },
    { key: "commission_pct", label: "Comisión %", render: (it) => `${Number(it.commission_pct) || 0}%` },
  ],
  fields: [
    { key: "name", label: "Nombre completo", span: 2 },
    { key: "role", label: "Cargo" },
    { key: "commercial_role", label: "Rol comercial", type: "select", allowEmpty: true,
      options: [{ value: "Decisor", label: "Decisor" }, { value: "Negociador", label: "Negociador" }, { value: "Influenciador", label: "Influenciador" }, { value: "Operativo", label: "Operativo" }] },
    { key: "owner_type", label: "Pertenece a", type: "select",
      options: [{ value: "client", label: "Cliente" }, { value: "brand", label: "Marca" }, { value: "agency", label: "Agencia" }] },
    { key: "owner_id", label: "Empresa", type: "select", allowEmpty: true,
      options: (l) => [
        ...(l.clients || []).map((c) => ({ value: c.id, label: `Cliente · ${c.name}` })),
        ...(l.agencies || []).map((a) => ({ value: a.id, label: `Agencia · ${a.name}` })),
      ] },
    { key: "brand_id", label: "Marca que maneja (opcional)", type: "select", allowEmpty: true,
      options: (l) => (l.brands || []).map((b) => ({ value: b.id, label: b.name })) },
    { key: "phone", label: "Teléfono" },
    { key: "whatsapp", label: "WhatsApp" },
    { key: "email", label: "Email", type: "email" },
    { key: "linkedin", label: "LinkedIn" },
    { key: "city", label: "Ciudad" },
    { key: "country", label: "País" },
    { key: "commission_pct", label: "Comisión ejecutivo %", type: "number", step: "0.1" },
    { key: "status", label: "Estado", type: "select", options: [{ value: "active", label: "Activo" }, { value: "inactive", label: "Inactivo" }] },
    { key: "observations", label: "Observaciones", type: "textarea", span: 2 },
  ],
};

export const SCHEMA_CATEGORIES = {
  title: "Categorías y Tipos",
  subtitle: "Categorías de productos y tipos de frecuencia",
  endpoint: "com/categories",
  defaults: { kind: "category", order: 0 },
  columns: [
    { key: "name", label: "Nombre" },
    { key: "kind", label: "Tipo" },
    { key: "order", label: "Orden" },
  ],
  fields: [
    { key: "name", label: "Nombre" },
    { key: "kind", label: "Clase", type: "select", options: [{ value: "category", label: "Categoría" }, { value: "type", label: "Tipo / frecuencia" }] },
    { key: "description", label: "Descripción", type: "textarea", span: 2 },
    { key: "order", label: "Orden", type: "number", step: "1" },
  ],
};

export const SCHEMA_PRODUCTS = {
  title: "Productos Comerciales",
  subtitle: "Espacios, acciones y servicios publicitarios del canal",
  endpoint: "com/products",
  defaults: { status: "active", currency: "BOB", allow_discount: true, max_discount_pct: 30 },
  columns: [
    { key: "name", label: "Producto" },
    { key: "category_id", label: "Categoría", render: (it, l) => (l.categories || []).find((c) => c.id === it.category_id)?.name || "—" },
    { key: "duration", label: "Duración" },
    { key: "frequency", label: "Frecuencia" },
    { key: "base_price", label: "Precio base", render: (it) => `${it.currency || "BOB"} ${(Number(it.base_price) || 0).toLocaleString()}` },
    { key: "status", label: "Estado" },
  ],
  fields: [
    { key: "name", label: "Nombre del producto", span: 2 },
    { key: "category_id", label: "Categoría", type: "select", allowEmpty: true,
      options: (l) => (l.categories || []).filter((c) => c.kind === "category").map((c) => ({ value: c.id, label: c.name })) },
    { key: "type_id", label: "Tipo / Frecuencia", type: "select", allowEmpty: true,
      options: (l) => (l.categories || []).filter((c) => c.kind === "type").map((c) => ({ value: c.id, label: c.name })) },
    { key: "format", label: "Formato" },
    { key: "platform", label: "Plataforma" },
    { key: "program_id", label: "Programa asociado (opcional)", type: "select", allowEmpty: true,
      options: (l) => (l.programs || []).map((p) => ({ value: p.id, label: p.name })) },
    { key: "talent_id", label: "Talento asociado (opcional)", type: "select", allowEmpty: true,
      options: (l) => (l.com_talents || []).map((t) => ({ value: t.id, label: t.artist_name })) },
    { key: "duration", label: "Duración estimada" },
    { key: "frequency", label: "Frecuencia" },
    { key: "base_price", label: "Precio base", type: "number" },
    { key: "currency", label: "Moneda", type: "currency" },
    { key: "internal_cost", label: "Costo interno estimado", type: "number" },
    { key: "margin_pct", label: "Margen estimado %", type: "number" },
    { key: "allow_discount", label: "Permite descuento", type: "switch" },
    { key: "max_discount_pct", label: "Descuento máximo %", type: "number" },
    { key: "image", label: "Imagen referencial (URL)" },
    { key: "short_description", label: "Descripción corta", type: "textarea", span: 2 },
    { key: "long_description", label: "Descripción larga", type: "textarea", rows: 5, span: 2 },
    { key: "status", label: "Estado", type: "select", options: [{ value: "active", label: "Activo" }, { value: "inactive", label: "Inactivo" }] },
    { key: "observations", label: "Observaciones", type: "textarea", span: 2 },
  ],
};

export const SCHEMA_TALENTS = {
  title: "Talentos / Influencers (comercial)",
  subtitle: "Catálogo comercial de talentos disponibles",
  endpoint: "com/talents",
  defaults: { status: "active", currency: "BOB", country: "Bolivia" },
  columns: [
    { key: "artist_name", label: "Talento" },
    { key: "category", label: "Categoría" },
    { key: "city", label: "Ciudad" },
    { key: "base_price", label: "Precio referencial", render: (it) => `${it.currency || "BOB"} ${(Number(it.base_price) || 0).toLocaleString()}` },
    { key: "status", label: "Estado" },
  ],
  fields: [
    { key: "artist_name", label: "Nombre artístico", span: 2 },
    { key: "real_name", label: "Nombre real" },
    { key: "category", label: "Categoría" },
    { key: "city", label: "Ciudad" },
    { key: "country", label: "País" },
    { key: "photo", label: "Foto (URL o /api/media/...)" },
    { key: "engagement_pct", label: "Engagement %", type: "number" },
    { key: "base_price", label: "Precio referencial", type: "number" },
    { key: "currency", label: "Moneda", type: "currency" },
    { key: "description", label: "Descripción", type: "textarea", span: 2 },
    { key: "status", label: "Estado", type: "select", options: [{ value: "active", label: "Activo" }, { value: "inactive", label: "Inactivo" }] },
    { key: "observations", label: "Observaciones", type: "textarea", span: 2 },
  ],
};
